import { WeatherSoilPestChangeDevicePopoverComponent } from '@/app/features/services/weather-soil-pest/shared/weather-soil-pest-change-device-popover/weather-soil-pest-change-device-popover.component';
import {
  DeviceLineCodesEnum,
  WeatherSoilPestChangeDeviceService,
} from '@/app/features/services/weather-soil-pest/shared/weather-soil-pest-change-device-popover/weather-soil-pest-change-device.service';
import { WeatherSoilPestErrorContainerComponent } from '@/app/features/services/weather-soil-pest/shared/weather-soil-pest-error-container/weather-soil-pest-error-container.component';
import { WeatherSoilPestNodataContainerComponent } from '@/app/features/services/weather-soil-pest/shared/weather-soil-pest-nodata-container/weather-soil-pest-nodata-container.component';
import {
  CropPhenologyProxy,
  GetPhenologyCameraImagesPaginatedQuery,
  GroupedDeviceInfo,
  GroupedPlantationViewModel,
  PhenologyCameraCropImagesResponse,
  PhenologyCameraCropImagesResponseResponseResult,
} from '@/app/proxy/proxies';
import { SharedModule } from '@/app/shared/shared.module';
import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { Swiper } from 'swiper/types';
import { NoPhenologyDeviceContainerComponent } from './no-phenology-device-container/no-phenology-device-container.component';
import { WeatherSoilPestAddConfirmDeviceComponent } from '@/app/features/services/weather-soil-pest/shared/weather-soil-pest-add-confirm-device/weather-soil-pest-add-confirm-device.component';
import { MixpanelTrackerService } from '@/app/core/analytics';
import { PlantationService } from '@/app/core/services/plantation.service';

@Component({
  selector: 'app-phenological-images',
  imports: [
    SharedModule,
    WeatherSoilPestChangeDevicePopoverComponent,
    WeatherSoilPestNodataContainerComponent,
    WeatherSoilPestErrorContainerComponent,
    NoPhenologyDeviceContainerComponent,
    WeatherSoilPestAddConfirmDeviceComponent,
  ],
  templateUrl: './phenological-images.component.html',
  styleUrl: './phenological-images.component.less',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [CropPhenologyProxy],
})
export class PhenologicalImagesComponent implements OnChanges, AfterViewInit, OnDestroy {
  private cropPhenologyProxy = inject(CropPhenologyProxy);
  weatherSoilPestChangeDeviceService = inject(WeatherSoilPestChangeDeviceService);
  private mixpanelTracker = inject(MixpanelTrackerService);
  private plantationService = inject(PlantationService);
  private destroy$ = new Subject<void>();

  // Loading states
  loading = false;
  loadMoreLoading = false;
  wasThereAnError = false;

  // Input data
  @Input() selectedPlantation: GroupedPlantationViewModel;
  selectedPhenology: GroupedDeviceInfo;

  // Pagination
  pageNumber = 1;
  pageSize = 10;
  totalCount = 0;

  // Slider data
  phenologicalImagesResponse: PhenologyCameraCropImagesResponse;
  activeSlideIndex = 0;
  maxIndex = 0;

  // ViewChildren
  @ViewChild('swiperContainer', { static: false }) swiperContainer!: ElementRef;
  @ViewChild('slidesContainerEl') slidesContainerEl!: ElementRef;
  @ViewChild(WeatherSoilPestChangeDevicePopoverComponent, { static: false })
  weatherSoilPestChangeDevicePopoverComponent!: WeatherSoilPestChangeDevicePopoverComponent;

  swiper: Swiper;
  deviceLineCodesEnum = DeviceLineCodesEnum;

  private isLoadingMore = false;
  private swiperInitialized = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['selectedPlantation']) {
      this.resetComponent();

      if (this.selectedPlantation?.activeRotationId) {
        this.subscribeToDeviceChanges();
      }
    }
  }

  ngAfterViewInit(): void {
    this.initializeSwiper();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupSwiper();
  }

  private resetComponent(): void {
    this.swiperInitialized = false;
    this.swiper = null;
    this.phenologicalImagesResponse = null;
    this.wasThereAnError = false;
    this.pageNumber = 1;
    this.activeSlideIndex = 0;
    this.maxIndex = 0;
  }

  private subscribeToDeviceChanges(): void {
    this.weatherSoilPestChangeDeviceService.selectedPhenologyDevice$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedPhenology) => {
        if (selectedPhenology && selectedPhenology.assetId !== this.selectedPhenology?.assetId) {
          this.selectedPhenology = selectedPhenology;
          this.resetComponent();
          this.getPhenologicalImages();
        }
      });
  }

  getPhenologicalImages(loadMore = false): void {
    // Prevent duplicate load more requests
    if (loadMore && this.isLoadingMore) {
      return;
    }

    // Check if we have more data to load
    if (loadMore && this.pageSize * this.pageNumber >= this.totalCount) {
      return;
    }

    this.wasThereAnError = false;

    if (!loadMore) {
      this.loading = true;
      this.pageNumber = 1;
    } else {
      this.loadMoreLoading = true;
      this.isLoadingMore = true;
    }

    const body = new GetPhenologyCameraImagesPaginatedQuery();
    body.rotationId = this.selectedPlantation?.activeRotationId;
    body.assetId = this.selectedPhenology.assetId;
    body.pageNumber = this.pageNumber;
    body.pageSize = this.pageSize;

    this.cropPhenologyProxy.cropPhenology_GetPhenologyCameraImagesPaginated(undefined, undefined, body)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.loadMoreLoading = false;
          this.isLoadingMore = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res: PhenologyCameraCropImagesResponseResponseResult) => {
          if (res.isSuccess) {
            this.handleSuccessResponse(res, loadMore);
          } else {
            this.wasThereAnError = true;
          }
        },
        error: () => {
          this.wasThereAnError = true;
        },
      });
  }

  private handleSuccessResponse(res: PhenologyCameraCropImagesResponseResponseResult, loadMore: boolean): void {
    let currentActiveIndex = 0;
    if (loadMore && this.swiper) {
      currentActiveIndex = this.swiper.activeIndex;
    }

    if (!loadMore) {
      this.phenologicalImagesResponse = res.result;
      this.pageSize = res.result.images.pageSize;
      this.totalCount = res.result.images.totalCount;
    } else {
      this.phenologicalImagesResponse.images.items = [
        ...this.phenologicalImagesResponse.images.items,
        ...res.result.images.items,
      ];
    }

    setTimeout(() => {
      this.initializeSwiper();
      if (loadMore && this.swiper) {
        this.swiper.slideTo(currentActiveIndex, 0, false);
      }
    }, 100);
  }

  private initializeSwiper(): void {
    const swiperEl = this.swiperContainer?.nativeElement as any;

    if (swiperEl?.swiper) {
      if (!this.swiperInitialized || this.swiper !== swiperEl.swiper) {
        this.swiper = swiperEl.swiper;
        this.swiperInitialized = true;
        this.setupSwiperEvents();
      }
      this.updateSwiperConfig();
    } else if (this.phenologicalImagesResponse?.images?.items?.length > 0) {
      setTimeout(() => this.initializeSwiper(), 100);
    }
  }

  private setupSwiperEvents(): void {
    if (!this.swiper) return;

    this.swiper.on('slideChange', (e: Swiper) => {
      this.activeSlideIndex = e.activeIndex;
    });

    this.swiper.on('reachEnd', () => {
      this.loadMore();
    });
  }

  private updateSwiperConfig(): void {
    if (!this.swiper || !this.phenologicalImagesResponse?.images?.items) return;

    const totalSlides = this.phenologicalImagesResponse.images.items.length;
    const slidesPerView = this.getSlidesPerView();

    this.maxIndex = Math.max(0, totalSlides - slidesPerView);
    this.swiper.update();
  }

  private getSlidesPerView(): number {
    if (!this.swiper) return 1;

    if (typeof this.swiper.params.slidesPerView === 'number') {
      return this.swiper.params.slidesPerView;
    }

    // if (this.swiper.params.slidesPerView === 'auto') {
    //   return this.swiper.slidesPerViewDynamic() || 1;
    // }

    return 1;
  }

  private loadMore(): void {
    if (this.canLoadMore()) {
      this.pageNumber += 1;
      this.getPhenologicalImages(true);
    }
  }

  private canLoadMore(): boolean {
    return !this.isLoadingMore && !this.loading && this.pageSize * this.pageNumber < this.totalCount;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateSwiperConfig();
  }

  goToNextSlide(): void {
    if (!this.swiper) {
      console.warn('Swiper not ready yet');
      return;
    }

    if (this.activeSlideIndex < this.maxIndex) {
      this.swiper.slideNext();
      this.trackPhenologyPhotoViewed();
    } else if (this.canLoadMore()) {
      this.loadMore();
      this.trackPhenologyPhotoViewed();
    }
  }

  goToPreviousSlide(): void {
    if (this.activeSlideIndex === 0) {
      return;
    }

    if (!this.swiper) {
      console.warn('Swiper not ready yet');
      return;
    }

    if (this.activeSlideIndex > 0) {
      this.swiper.slidePrev();
      this.trackPhenologyPhotoViewed();
    }
  }

  private trackPhenologyPhotoViewed(): void {
    if (!this.selectedPlantation) {
      return;
    }

    const land = this.plantationService.selectedLand;
    const enterpriseId = land?.enterpriseId || this.plantationService.enterpriseId;
    const enterprise = this.plantationService.originalResult?.find((item) => item.id === enterpriseId);

    this.mixpanelTracker.trackServicesPhenologyPhotoViewed({
      'focus view': false,
      'phenology camera id': this.selectedPhenology?.assetId ?? '',
      'plantation id': this.selectedPlantation.id,
      'plantation name': this.selectedPlantation.name,
      'land id': land?.id,
      'land name': land?.name,
      'enterprise id': enterpriseId,
      'enterprise name': enterprise?.enterpriseName,
    });
  }

  onClickChangeDeviceBtn(event: Event): void {
    this.weatherSoilPestChangeDevicePopoverComponent.popover.toggle(event);
  }

  private cleanupSwiper(): void {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
      this.swiperInitialized = false;
    }
  }

  get hasMoreImages(): boolean {
    return this.pageSize * this.pageNumber < this.totalCount;
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }
}
